import { useEffect, useRef, useCallback } from 'react';

const WARN_MS = 4 * 60 * 1000;          // 4 minutes -> show warning
const IDLE_MS = 4.5 * 60 * 1000;        // 4.5 minutes -> auto-logout

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
] as const;

export function useInactivityTimer(onWarn: () => void, onTimeout: () => void, onDismiss?: () => void) {
  const warnCb    = useRef(onWarn);
  const timeoutCb = useRef(onTimeout);
  const dismissCb = useRef(onDismiss);
  warnCb.current    = onWarn;
  timeoutCb.current = onTimeout;
  dismissCb.current = onDismiss;

  const warnTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    const wasWarningActive = warnTimer.current !== null;
    if (warnTimer.current)    clearTimeout(warnTimer.current);
    if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    warnTimer.current    = setTimeout(() => warnCb.current(),    WARN_MS);
    timeoutTimer.current = setTimeout(() => timeoutCb.current(), IDLE_MS);
    if (wasWarningActive && dismissCb.current) {
      dismissCb.current();
    }
  }, []);

  useEffect(() => {
    const handle = () => reset();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handle, { passive: true }));
    reset();
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handle));
      if (warnTimer.current)    clearTimeout(warnTimer.current);
      if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    };
  }, [reset]);

  return { reset };
}
